import { Inject, Injectable, Optional } from "@angular/core";
import { MAT_DATE_LOCALE, NativeDateAdapter } from "@angular/material/core";

/**
 * Adapter de datas independente de fuso horário.
 *
 * Contexto: as colunas de data escolhidas pelo usuário são `@db.Date` (data
 * pura) no Postgres e o backend roda em UTC. O Postgres guarda a porção de
 * data em UTC do timestamp recebido. Como há clientes em fusos diferentes
 * (não só Brasil/UTC-3), qualquer offset fixo está errado.
 *
 * Estratégia: TODO valor de datepicker é ancorado ao MEIO-DIA local do
 * dia-calendário escolhido. Meio-dia local nunca cruza o limite do dia ao
 * converter para UTC (toISOString, feito pelo HttpClient ao enviar à API)
 * em nenhum fuso das Américas, preservando o dia escolhido em `@db.Date`.
 */
@Injectable()
export class AdapterDate extends NativeDateAdapter {

    constructor(@Optional() @Inject(MAT_DATE_LOCALE) dateLocale: string) {
        super(dateLocale);
    }

    /** Seleção no calendário -> meio-dia local. */
    createDate(year: number, month: number, date: number): Date {
        return new Date(year, month, date, 12, 0, 0, 0);
    }

    /**
     * Formata SEM mutar o Date do formulário.
     *
     * O Angular chama format() a cada renderização; mutar o Date aqui (como
     * a versão antiga, que somava horas) fazia o valor "andar" e pular o dia.
     */
    format(date: Date, _displayFormat: Object): string {
        const day = `${date.getDate()}`.padStart(2, "0");
        const month = `${date.getMonth() + 1}`.padStart(2, "0");

        return `${day}/${month}/${date.getFullYear()}`;
    }

    /** Entrada digitada em dd/MM/yyyy -> meio-dia local. */
    parse(value: any, _parseFormat?: any): Date | null {
        if (!value) {
            return null;
        }

        if (typeof value === "string") {
            const match = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

            if (match) {
                return new Date(+match[3], +match[2] - 1, +match[1], 12, 0, 0, 0);
            }
        }

        const parsed = new Date(value);

        return isNaN(parsed.getTime()) ? null : parsed;
    }

    /**
     * Normaliza QUALQUER valor (string da API/localStorage, Date, etc.) para o
     * dia-calendário correto ancorado ao meio-dia local — independente do fuso.
     *
     * É o ponto único por onde todo valor passa ao ser atribuído ao datepicker
     * (formBuilder/patchValue/setValue), então corrige inclusive as telas que
     * hoje jogam a string crua direto no controle (off-by-one na exibição).
     */
    deserialize(value: any): Date | null {
        if (value === null || value === undefined || value === "") {
            return null;
        }

        // Strings ISO ("YYYY-MM-DD" ou "YYYY-MM-DDTHH:mm:ssZ"): usa apenas a
        // porção de data, ignorando hora/fuso, e ancora no meio-dia local.
        if (typeof value === "string") {
            const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

            if (match) {
                return new Date(+match[1], +match[2] - 1, +match[3], 12, 0, 0, 0);
            }

            const parsed = new Date(value);

            return isNaN(parsed.getTime()) ? null : parsed;
        }

        // Date: re-ancora ao meio-dia local mantendo o dia-calendário local.
        if (value instanceof Date) {
            return new Date(
                value.getFullYear(),
                value.getMonth(),
                value.getDate(),
                12, 0, 0, 0,
            );
        }

        const parsed = new Date(value);

        return isNaN(parsed.getTime()) ? null : parsed;
    }

}
